import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import CompanyAutocomplete from '../components/CompanyAutocomplete/CompanyAutocomplete';
import axios from 'axios';
import './AuthPages.scss';

interface CVTemplate {
  id: number;
  category: string;
  value: string;
  label: string;
}

interface EmployeeRegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone: string;
  acceptTerms: boolean;
}

interface EmployerRegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  companyCode: string;
  companyActivity: string;
  contactPerson: string;
  phone: string;
  isPersonalDataCompany: boolean;
  acceptEmailNotifications: boolean;
  acceptTerms: boolean;
}

const RegisterPage: React.FC = () => {
  const { register, googleLogin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const userType = searchParams.get('type') === 'employer' ? 'employer' : 'employee';
  const returnUrl = searchParams.get('return') || '/';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [employeeForm, setEmployeeForm] = useState<EmployeeRegisterForm>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    acceptTerms: false
  });

  const [employerForm, setEmployerForm] = useState<EmployerRegisterForm>({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    companyCode: '',
    companyActivity: '',
    contactPerson: '',
    phone: '',
    isPersonalDataCompany: false,
    acceptEmailNotifications: false,
    acceptTerms: false
  });

  const [companyActivityOptions, setCompanyActivityOptions] = useState<CVTemplate[]>([]);

  // Load company activity options from CV templates
  useEffect(() => {
    const loadCompanyActivityOptions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/cv-templates?grouped=true`);
        if (response.data?.data?.company_activity) {
          setCompanyActivityOptions(response.data.data.company_activity);
        }
      } catch (error) {
        console.error('Error loading company activity options:', error);
      }
    };
    loadCompanyActivityOptions();
  }, []);

  // Redirect if already authenticated (only on initial page load)
  const hasRegistered = React.useRef(false);
  React.useEffect(() => {
    if (isAuthenticated && !hasRegistered.current) {
      navigate(returnUrl);
    }
  }, [isAuthenticated, navigate, returnUrl]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (password.length < 8) errors.push('mažiausiai 8 simboliai');
    if (!/[A-Z]/.test(password)) errors.push('bent 1 didžioji raidė');
    if (!/[a-z]/.test(password)) errors.push('bent 1 mažoji raidė');
    if (!/\d/.test(password)) errors.push('bent 1 skaičius');
    return { valid: errors.length === 0, errors };
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Silpnas', color: '#ef4444' };
    if (strength <= 3) return { strength, label: 'Vidutinis', color: '#f59e0b' };
    return { strength, label: 'Stiprus', color: '#22c55e' };
  };

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    const form = userType === 'employee' ? employeeForm : employerForm;

    if (!form.email) {
      newErrors.email = 'El. paštas privalomas';
    } else if (!validateEmail(form.email)) {
      newErrors.email = 'Neteisingas el. pašto formatas';
    }

    if (!form.password) {
      newErrors.password = 'Slaptažodis privalomas';
    } else {
      const passwordValidation = validatePassword(form.password);
      if (!passwordValidation.valid) {
        newErrors.password = `Slaptažodžiui trūksta: ${passwordValidation.errors.join(', ')}`;
      }
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = 'Pakartokite slaptažodį';
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = 'Slaptažodžiai nesutampa';
    }

    if (userType === 'employee') {
      if (!employeeForm.firstName) newErrors.firstName = 'Vardas privalomas';
      if (!employeeForm.lastName) newErrors.lastName = 'Pavardė privaloma';
      if (!employeeForm.phone) newErrors.phone = 'Telefono numeris privalomas';
    } else {
      if (!employerForm.companyName) newErrors.companyName = 'Įmonės pavadinimas privalomas';
      if (!employerForm.companyCode) newErrors.companyCode = 'Įmonės kodas privalomas';
      if (!employerForm.companyActivity) newErrors.companyActivity = 'Įmonės veiklos sritis privaloma';
      if (!employerForm.contactPerson) newErrors.contactPerson = 'Kontaktinis asmuo privalomas';
      if (!employerForm.phone) newErrors.phone = 'Telefono numeris privalomas';
    }

    if (!form.acceptTerms) {
      newErrors.acceptTerms = 'Privaloma sutikti su naudojimo taisyklėmis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [userType, employeeForm, employerForm]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});
    hasRegistered.current = true;

    try {
      const form = userType === 'employee' ? employeeForm : employerForm;
      
      const registerData = {
        email: form.email,
        password: form.password,
        confirmPassword: form.confirmPassword,
        userType: userType,
        firstName: userType === 'employee' ? employeeForm.firstName : undefined,
        lastName: userType === 'employee' ? employeeForm.lastName : undefined,
        phone: form.phone,
        companyName: userType === 'employer' ? employerForm.companyName : undefined,
        companyCode: userType === 'employer' ? employerForm.companyCode : undefined,
        companyActivity: userType === 'employer' ? employerForm.companyActivity : undefined,
        contactPerson: userType === 'employer' ? employerForm.contactPerson : undefined,
        isRecruitmentAgency: userType === 'employer' ? employerForm.isPersonalDataCompany : undefined,
        acceptTerms: form.acceptTerms,
      };

      const result = await register(registerData);
      
      if (result.success) {
        window.location.href = returnUrl !== '/' ? returnUrl : (result.redirectPath || returnUrl);
        return;
      } else {
        hasRegistered.current = false;
        setErrors({ general: result.message || 'Registracija nepavyko' });
      }
    } catch (error) {
      hasRegistered.current = false;
      setErrors({ general: 'Registracijos klaida. Bandykite dar kartą.' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) return;
    
    setLoading(true);
    try {
      const result = await googleLogin(credentialResponse.credential, userType);
      
      if (result.success) {
        const destination = returnUrl !== '/' ? returnUrl : (result.redirectPath || returnUrl);
        window.location.href = destination;
      } else {
        setErrors({ general: result.message });
      }
    } catch (error) {
      setErrors({ general: 'Google registracija nepavyko' });
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeFormChange = useCallback((field: keyof EmployeeRegisterForm, value: string | boolean) => {
    setEmployeeForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleEmployerFormChange = useCallback((field: keyof EmployerRegisterForm, value: string | boolean) => {
    setEmployerForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Register Card */}
        <div className="auth-card register-card">
          <div className="auth-header">
            <h1>Registracija</h1>
            <p>{userType === 'employee' ? 'Sukurkite darbuotojo paskyrą' : 'Sukurkite darbdavio paskyrą'}</p>
          </div>

          {errors.general && (
            <div className="error-alert">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleRegister} className="auth-form">
            {userType === 'employee' ? (
              <>
                {/* Employee Registration Form */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">Vardas *</label>
                    <input
                      type="text"
                      id="firstName"
                      value={employeeForm.firstName}
                      onChange={(e) => handleEmployeeFormChange('firstName', e.target.value)}
                      className={errors.firstName ? 'error' : ''}
                      placeholder="Jūsų vardas"
                    />
                    {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Pavardė *</label>
                    <input
                      type="text"
                      id="lastName"
                      value={employeeForm.lastName}
                      onChange={(e) => handleEmployeeFormChange('lastName', e.target.value)}
                      className={errors.lastName ? 'error' : ''}
                      placeholder="Jūsų pavardė"
                    />
                    {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">El. paštas *</label>
                  <input
                    type="email"
                    id="email"
                    value={employeeForm.email}
                    onChange={(e) => handleEmployeeFormChange('email', e.target.value)}
                    className={errors.email ? 'error' : ''}
                    placeholder="jusu@email.com"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Telefono numeris *</label>
                  <input
                    type="tel"
                    id="phone"
                    value={employeeForm.phone}
                    onChange={(e) => handleEmployeeFormChange('phone', e.target.value)}
                    className={errors.phone ? 'error' : ''}
                    placeholder="+370 600 00000"
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">Slaptažodis *</label>
                    <div className="password-input">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={employeeForm.password}
                        onChange={(e) => handleEmployeeFormChange('password', e.target.value)}
                        className={errors.password ? 'error' : ''}
                        placeholder="Pvz.: Slaptazodis123"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {employeeForm.password && (
                      <div className="password-strength">
                        <div className="strength-bar">
                          <div 
                            className="strength-fill" 
                            style={{ 
                              width: `${(getPasswordStrength(employeeForm.password).strength / 5) * 100}%`,
                              backgroundColor: getPasswordStrength(employeeForm.password).color 
                            }}
                          />
                        </div>
                        <span style={{ color: getPasswordStrength(employeeForm.password).color, fontSize: '12px' }}>
                          {getPasswordStrength(employeeForm.password).label}
                        </span>
                      </div>
                    )}
                    <div className="password-requirements">
                      <small>
                        <span className={employeeForm.password.length >= 8 ? 'requirement-met' : ''}>✓ 8+ simbolių</span>
                        {' • '}
                        <span className={/[A-Z]/.test(employeeForm.password) ? 'requirement-met' : ''}>✓ Didžioji raidė</span>
                        {' • '}
                        <span className={/[a-z]/.test(employeeForm.password) ? 'requirement-met' : ''}>✓ Mažoji raidė</span>
                        {' • '}
                        <span className={/\d/.test(employeeForm.password) ? 'requirement-met' : ''}>✓ Skaičius</span>
                      </small>
                    </div>
                    {errors.password && <span className="error-message">{errors.password}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Pakartoti slaptažodį *</label>
                    <div className="password-input">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={employeeForm.confirmPassword}
                        onChange={(e) => handleEmployeeFormChange('confirmPassword', e.target.value)}
                        className={errors.confirmPassword ? 'error' : ''}
                        placeholder="Pakartokite slaptažodį"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={employeeForm.acceptTerms}
                      onChange={(e) => handleEmployeeFormChange('acceptTerms', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    <span>
                      Sutinku su <Link to="/naudojimosi-salygos" target="_blank">naudojimo taisyklėmis</Link> ir{' '}
                      <Link to="/privatumo-politika" target="_blank">privatumo politika</Link>
                    </span>
                  </label>
                  {errors.acceptTerms && <span className="error-message">{errors.acceptTerms}</span>}
                </div>
              </>
            ) : (
              <>
                {/* Employer Registration Form */}
                <div className="form-group">
                  <label htmlFor="companyName">Įmonės pavadinimas *</label>
                  <CompanyAutocomplete
                    id="companyName"
                    value={employerForm.companyName}
                    companyCode={employerForm.companyCode}
                    onCompanySelect={(name, code, activity) => {
                      setEmployerForm(prev => ({
                        ...prev,
                        companyName: name,
                        companyCode: code,
                        companyActivity: activity || prev.companyActivity,
                      }));
                      if (errors.companyName) setErrors(prev => ({ ...prev, companyName: '' }));
                      if (errors.companyCode) setErrors(prev => ({ ...prev, companyCode: '' }));
                    }}
                    onManualInput={(name) => {
                      handleEmployerFormChange('companyName', name);
                    }}
                    error={!!errors.companyName}
                    placeholder="Ieškokite arba įveskite įmonės pavadinimą"
                  />
                  {errors.companyName && <span className="error-message">{errors.companyName}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companyCode">Įmonės kodas *</label>
                    <input
                      type="text"
                      id="companyCode"
                      value={employerForm.companyCode}
                      onChange={(e) => handleEmployerFormChange('companyCode', e.target.value)}
                      className={errors.companyCode ? 'error' : ''}
                      placeholder="123456789"
                    />
                    {errors.companyCode && <span className="error-message">{errors.companyCode}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companyActivity">Įmonės veiklos sritis *</label>
                    <select
                      id="companyActivity"
                      value={employerForm.companyActivity}
                      onChange={(e) => handleEmployerFormChange('companyActivity', e.target.value)}
                      className={errors.companyActivity ? 'error' : ''}
                    >
                      <option value="">Pasirinkite sritį</option>
                      {companyActivityOptions.map((option) => (
                        <option key={option.id} value={option.value}>
                          {option.value}
                        </option>
                      ))}
                    </select>
                    {errors.companyActivity && <span className="error-message">{errors.companyActivity}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactPerson">Kontaktinis asmuo *</label>
                    <input
                      type="text"
                      id="contactPerson"
                      value={employerForm.contactPerson}
                      onChange={(e) => handleEmployerFormChange('contactPerson', e.target.value)}
                      className={errors.contactPerson ? 'error' : ''}
                      placeholder="Vardas Pavardė"
                    />
                    {errors.contactPerson && <span className="error-message">{errors.contactPerson}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">El. paštas *</label>
                    <input
                      type="email"
                      id="email"
                      value={employerForm.email}
                      onChange={(e) => handleEmployerFormChange('email', e.target.value)}
                      className={errors.email ? 'error' : ''}
                      placeholder="info@imone.lt"
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Telefonas *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={employerForm.phone}
                      onChange={(e) => handleEmployerFormChange('phone', e.target.value)}
                      className={errors.phone ? 'error' : ''}
                      placeholder="+370 600 00000"
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">Slaptažodis *</label>
                    <div className="password-input">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={employerForm.password}
                        onChange={(e) => handleEmployerFormChange('password', e.target.value)}
                        className={errors.password ? 'error' : ''}
                        placeholder="Pvz.: Slaptazodis123"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {employerForm.password && (
                      <div className="password-strength">
                        <div className="strength-bar">
                          <div 
                            className="strength-fill" 
                            style={{ 
                              width: `${(getPasswordStrength(employerForm.password).strength / 5) * 100}%`,
                              backgroundColor: getPasswordStrength(employerForm.password).color 
                            }}
                          />
                        </div>
                        <span style={{ color: getPasswordStrength(employerForm.password).color, fontSize: '12px' }}>
                          {getPasswordStrength(employerForm.password).label}
                        </span>
                      </div>
                    )}
                    <div className="password-requirements">
                      <small>
                        <span className={employerForm.password.length >= 8 ? 'requirement-met' : ''}>✓ 8+ simbolių</span>
                        {' • '}
                        <span className={/[A-Z]/.test(employerForm.password) ? 'requirement-met' : ''}>✓ Didžioji raidė</span>
                        {' • '}
                        <span className={/[a-z]/.test(employerForm.password) ? 'requirement-met' : ''}>✓ Mažoji raidė</span>
                        {' • '}
                        <span className={/\d/.test(employerForm.password) ? 'requirement-met' : ''}>✓ Skaičius</span>
                      </small>
                    </div>
                    {errors.password && <span className="error-message">{errors.password}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Pakartoti slaptažodį *</label>
                    <div className="password-input">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={employerForm.confirmPassword}
                        onChange={(e) => handleEmployerFormChange('confirmPassword', e.target.value)}
                        className={errors.confirmPassword ? 'error' : ''}
                        placeholder="Pakartokite slaptažodį"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={employerForm.isPersonalDataCompany}
                      onChange={(e) => handleEmployerFormChange('isPersonalDataCompany', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    <span>Personalo atrankų įmonė</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={employerForm.acceptEmailNotifications}
                      onChange={(e) => handleEmployerFormChange('acceptEmailNotifications', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    <span>Sutinku gauti cvgidas.lt naujienas el. paštu</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={employerForm.acceptTerms}
                      onChange={(e) => handleEmployerFormChange('acceptTerms', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    <span>
                      Sutinku su <Link to="/naudojimosi-salygos" target="_blank">naudojimo taisyklėmis</Link> ir{' '}
                      <Link to="/privatumo-politika" target="_blank">privatumo politika</Link>
                    </span>
                  </label>
                  {errors.acceptTerms && <span className="error-message">{errors.acceptTerms}</span>}
                </div>
              </>
            )}

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Registruojama...' : 'Registruotis'}
            </button>

            <div className="divider">
              <span>arba</span>
            </div>

            <div className="google-login-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => console.warn('Google Sign-In error')}
                text="signup_with"
                shape="rectangular"
                size="large"
              />
            </div>
          </form>

          <div className="auth-footer">
            <p>
              Jau turite paskyrą?{' '}
              <Link to={`/login?type=${userType}&mode=${userType}${returnUrl !== '/' ? `&return=${encodeURIComponent(returnUrl)}` : ''}`}>
                Prisijunkite
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;

